/**
 * What: Provides the shared Shopify App Home resource-index shell for searchable tables.
 * Transport / data-flow: Owns table structure and list-variant column slimming; callers own row data,
 * filtering/sorting and per-row lifecycle preparation. Columns own cell content.
 *
 */
import { ChoiceList, IndexFilters, IndexFiltersMode, IndexTable, BlockStack } from '@shopify/polaris'
import { shopifyComponentCopy, shopifyIndexCopy } from '@testfeed/product-language'
import { Fragment, useState, type ComponentProps, type ReactNode } from 'react'
import type { IndexView, ResourceIndexSchema } from './resource-index/indexSchema'
import {
  applyIndexSelection,
  selectableRowIds,
  type ResourceIndexSelection as ResourceIndexSelectionModel,
} from './resource-index/ResourceIndexSelection'
import { useIndexTableCondensed } from '../hooks/useIndexTableCondensed'
import { useAppBridge } from '@shopify/app-bridge-react'
import { trackShopifyAction } from '../shopifyUiTracking'
import './ShopifyResourceIndex.css'

export type ShopifyResourceIndexFacet = {
  key: string
  label: string
  options: Array<{ value: string; label: string }>
  values: string[]
  onChange: (values: string[]) => void
  multiple?: boolean
}
export type ShopifyResourceIndexAppliedFilter = { key: string; label: string; onRemove: () => void }
export type ShopifyResourceIndexSortOption = { label: string; value: string }
type ShopifyResourceIndexPagination = NonNullable<ComponentProps<typeof IndexTable>['pagination']>
type ShopifyResourceIndexProps<T, R> = {
  rows: T[]
  renderers: Record<string, (row: R) => ReactNode>
  getRowId: (row: T) => string
  prepareRow: (row: T, render: (model: R) => ReactNode) => ReactNode
  onRowAction?: (model: R) => void
  getRowAction?: (model: R) => (() => void) | undefined
  isLoading?: boolean
  views?: IndexView[]
  selectedView?: string
  onSelectView?: (id: string) => void
  sortOptions?: ShopifyResourceIndexSortOption[]
  sortSelected?: string[]
  onSort?: (value: string[]) => void
  searchLabel?: string
  queryValue?: string
  onQueryChange?: (value: string) => void
  facets?: ShopifyResourceIndexFacet[]
  appliedFilters?: ShopifyResourceIndexAppliedFilter[]
  onClearFilters?: () => void
  filteredEmptyState?: ReactNode
  pagination?: ShopifyResourceIndexPagination
  /** Parsed JSON definition. Runtime cell renderers are supplied separately. */
  definition: ResourceIndexSchema
  selection?: ResourceIndexSelectionModel<T>
  onDeleteSelected?: () => void
}

/** Renders headers and cells from one visible column sequence, preserving positional pairing. */
export function ShopifyResourceIndex<T, R>({
  rows,
  getRowId,
  prepareRow,
  isLoading = false,
  views = [],
  selectedView,
  onSelectView = () => undefined,
  sortOptions = [],
  sortSelected = [],
  onSort = () => undefined,
  searchLabel,
  queryValue = '',
  onQueryChange = () => undefined,
  facets = [],
  appliedFilters = [],
  onClearFilters,
  filteredEmptyState,
  pagination,
  definition,
  renderers,
  onRowAction,
  getRowAction,
  selection,
  onDeleteSelected,
}: ShopifyResourceIndexProps<T, R>) {
  const shopify = useAppBridge()
  const [mode, setMode] = useState<IndexFiltersMode>(IndexFiltersMode.Default)
  const condensed = useIndexTableCondensed()
  const rendererRegistry = renderers
  const configuredColumns = definition.columns
  const visibleColumns = configuredColumns
    .map((configured) => {
      const renderer = rendererRegistry[configured.id]
      if (typeof renderer !== 'function') {
        throw new Error(`Missing resource-index renderer for column "${configured.id}"`)
      }
      return {
        id: configured.id,
        heading: configured.heading,
        kind: configured.kind,
        listSlot: configured.listSlot,
        showInList: configured.showInList,
        toneMap: configured.toneMap,
        renderCell: renderer,
      }
    })
    .filter((column) => !condensed || column.showInList !== false)
  const configuredViews = (definition.views ?? views).map((view) => ({
    ...view,
    label: views.find((runtime) => runtime.id === view.id)?.label ?? view.label,
  }))
  const configuredSortOptions = definition.sortOptions ?? sortOptions
  const configuredSearchLabel = definition.searchLabel ?? searchLabel
  const selectedTab = Math.max(
    0,
    configuredViews.findIndex((view) => view.id === selectedView),
  )
  const selectableIds = selection ? selectableRowIds(rows, getRowId, selection) : []
  const selectedSelectableIds = selection?.selectedIds.filter((id) => selectableIds.includes(id)) ?? []
  const selectAllChecked = selectableIds.length > 0 && selectedSelectableIds.length === selectableIds.length
  const selectAllIndeterminate = selectedSelectableIds.length > 0 && !selectAllChecked
  const tabs = configuredViews.map((view) => ({ content: view.label, id: view.id }))
  const headings = [
    ...(selection && condensed ? [{ title: '' }] : []),
    ...visibleColumns.map((column) => ({ title: column.heading })),
  ]
  const polarisSortOptions = configuredSortOptions.map((option) => ({
    label: option.label,
    value: option.value as `${string} asc` | `${string} desc`,
    directionLabel: option.value.endsWith(' desc') ? shopifyIndexCopy.descending : shopifyIndexCopy.ascending,
  }))
  const resourceName = definition.resource ?? { singular: 'item', plural: 'items' }
  const hasControls = Boolean(configuredSearchLabel || tabs.length || configuredSortOptions.length || facets.length)
  const selectionCopy = shopifyComponentCopy.studySetupBulkDeleteDialog
  const changeCompactSelection = (checked: boolean, ids: string[]) => {
    if (!selection) return
    const next = new Set(selection.selectedIds)
    ids.forEach((id) => (checked ? next.add(id) : next.delete(id)))
    selection.onChange([...next])
  }
  const configuredFilters = (definition.filters ?? []).map((filter) => {
    const runtime = facets.find((facet) => facet.key === filter.key)
    if (!runtime) throw new Error(`Missing resource-index facet for "${filter.key}"`)
    return {
      key: filter.key,
      label: filter.label,
      filter: (
        <ChoiceList
          title={filter.label}
          titleHidden
          choices={runtime.options ?? filter.options ?? []}
          selected={runtime.values}
          onChange={(values) => trackShopifyAction(shopify, 'select-filter', () => runtime.onChange(values))}
          allowMultiple={filter.multiple ?? true}
        />
      ),
    }
  })

  return (
    <BlockStack gap="0">
      {hasControls && (
        <IndexFilters
          queryValue={queryValue}
          queryPlaceholder={configuredSearchLabel}
          onQueryChange={onQueryChange}
          onQueryClear={() => trackShopifyAction(shopify, 'clear-filter', () => onQueryChange(''))}
          tabs={tabs}
          selected={selectedTab}
          onSelect={(index) =>
            trackShopifyAction(shopify, 'select-tab', () =>
              onSelectView(tabs[index]?.id ?? configuredViews[0]?.id ?? ''),
            )
          }
          canCreateNewView={false}
          filters={configuredFilters}
          appliedFilters={appliedFilters.map((filter) => ({
            key: filter.key,
            label: filter.label,
            onRemove: () => trackShopifyAction(shopify, 'clear-filter', filter.onRemove),
          }))}
          onClearAll={() => trackShopifyAction(shopify, 'clear-filter', onClearFilters ?? (() => undefined))}
          mode={mode}
          setMode={setMode}
          disableStickyMode={condensed}
          sortOptions={polarisSortOptions}
          sortSelected={sortSelected}
          onSort={(value) => trackShopifyAction(shopify, 'select-sort', () => onSort(value))}
          hideFilters={configuredFilters.length === 0}
        />
      )}
      {selection && condensed && selectableIds.length > 0 && (
        <div className="shopify-resource-index__selection-toolbar">
          <s-checkbox
            label={selectionCopy.selectAllEligible(resourceName.plural, selectableIds.length)}
            checked={selectAllChecked}
            indeterminate={selectAllIndeterminate}
            onInput={(event) => changeCompactSelection(event.currentTarget.checked, selectableIds)}
          />
          {selectedSelectableIds.length > 0 && (
            <>
              <span>{selectionCopy.selectedCount(selectedSelectableIds.length)}</span>
              <s-button variant="primary" onClick={onDeleteSelected}>
                {selectionCopy.deleteSelected(resourceName.plural)}
              </s-button>
              <s-button variant="tertiary" onClick={() => selection.onChange([])}>
                {selectionCopy.clearSelection}
              </s-button>
            </>
          )}
        </div>
      )}
      <div
        data-testid="shopify-resource-index"
        className={selection && condensed ? 'shopify-resource-index--selectable' : undefined}
      >
        <IndexTable
          resourceName={resourceName}
          itemCount={selection && !condensed && selectableIds.length > 0 ? selectableIds.length : rows.length}
          selectedItemsCount={selection ? selectedSelectableIds.length : 0}
          onSelectionChange={
            selection
              ? (type, toggle, target) => applyIndexSelection({ rows, selection, getRowId, type, toggle, target })
              : undefined
          }
          selectable={Boolean(selection && !condensed && selectableIds.length > 0)}
          promotedBulkActions={
            selection && !condensed && onDeleteSelected
              ? [
                  {
                    content: shopifyComponentCopy.studySetupBulkDeleteDialog.deleteSelected(resourceName.plural),
                    onAction: onDeleteSelected,
                  },
                ]
              : undefined
          }
          headings={headings as [{ title: string }, ...{ title: string }[]]}
          pagination={pagination}
          condensed={condensed}
          loading={isLoading}
          emptyState={rows.length === 0 ? filteredEmptyState : undefined}
        >
          {rows.map((row, position) => (
            <Fragment key={getRowId(row)}>
              {prepareRow(row, (model) => (
                <IndexTable.Row
                  id={getRowId(row)}
                  position={position}
                  key={getRowId(row)}
                  selected={selection ? selectedSelectableIds.includes(getRowId(row)) : false}
                  hideSelectable={selection ? !(selection.getSelectable?.(row) ?? true) : false}
                  accessibilityLabel={
                    selection
                      ? shopifyComponentCopy.studySetupBulkDeleteDialog.selectItem(
                          selection.getLabel?.(row) ?? resourceName.singular,
                        )
                      : undefined
                  }
                  onClick={getRowAction ? getRowAction(model) : onRowAction ? () => onRowAction(model) : undefined}
                >
                  {selection && condensed && (
                    <IndexTable.Cell className="shopify-resource-index__cell shopify-resource-index__cell--selection">
                      {(selection.getSelectable?.(row) ?? true) && (
                        <div onClick={(event) => event.stopPropagation()}>
                          <s-checkbox
                            label={selectionCopy.selectItem(selection.getLabel?.(row) ?? resourceName.singular)}
                            labelAccessibilityVisibility="exclusive"
                            checked={selectedSelectableIds.includes(getRowId(row))}
                            onInput={(event) => {
                              event.stopPropagation()
                              changeCompactSelection(event.currentTarget.checked, [getRowId(row)])
                            }}
                          />
                        </div>
                      )}
                    </IndexTable.Cell>
                  )}
                  {visibleColumns.map((column) => (
                    <IndexTable.Cell
                      key={column.id}
                      className={[
                        'shopify-resource-index__cell',
                        `shopify-resource-index__cell--${column.listSlot ?? 'default'}`,
                        `shopify-resource-index__cell--${column.id}`,
                      ].join(' ')}
                    >
                      {column.renderCell(model)}
                    </IndexTable.Cell>
                  ))}
                </IndexTable.Row>
              ))}
            </Fragment>
          ))}
        </IndexTable>
      </div>
    </BlockStack>
  )
}
